import { type ElementType, type ReactNode } from "react"
import { FeaturedIcon, type FeaturedIconColor } from "./featuredIcon"
import { type Icon } from "./iconCore"

export type EmptyStateProps = {
  /**
   * The main line of the EmptyState. This should be short and sweet - ideally
   * one line.
   */
  title: ReactNode

  /**
   * Optionally provide a wrapper for the title. By default the title is in a <p> tag
   * but it can be useful to put the title in another element for example if it is
   * reasonably a heading on the page.
   */
  titleWrapper?: ElementType

  /**
   * The color of the FeaturedIcon
   */
  "data-icon-color"?: FeaturedIconColor

  /**
   * The Icon to be displayed in the FeaturedIcon component
   */
  icon?: Icon

  /**
   * Optionally provide a longer description for the EmptyState to give more detail
   * beyond what is in the title. This can be several sentences long.
   */
  description?: ReactNode

  /**
   * Optionally provide a wrapper for the description. By default the description is in a <p> tag
   * but it can be useful to put the description in another element for example if it needs to
   * contain buttons.
   */
  descriptionWrapper?: ElementType

  /**
   * Optionally provide children that will be appended after the above content.
   * This is most useful for providing actions that the user can take.
   */
  children?: ReactNode

  /**
   * An optional className to be applied to the outer wrapper of the empty state.
   * This allows for additional styling if necessary. Note that positioning should
   * ideally be applied via a container component rather than to the EmptyState itself.
   * Still this may be useful in unusual circumstances.
   */
  className?: string
}
export default EmptyStateProps

export function EmptyState({
  icon,
  title,
  titleWrapper,
  description,
  descriptionWrapper,
  children,
  className,
  ...props
}: EmptyStateProps) {
  const TitleWrapper = titleWrapper ?? "p"
  const DescriptionWrapper = descriptionWrapper ?? "p"

  return (
    <div className={`empty-state ${className ?? ""}`}>
      {icon && (
        <FeaturedIcon data-color={props["data-icon-color"]} icon={icon} />
      )}
      <div className="empty-state-content">
        <TitleWrapper className="title-lg text-color-primary-subdued">
          {title}
        </TitleWrapper>
        {description && (
          <DescriptionWrapper className="body-md text-color-secondary">
            {description}
          </DescriptionWrapper>
        )}
      </div>
      {children}
    </div>
  )
}
